import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from 'react-redux'; // 1. Import useSelector

import Slider from "../../../components/ui/Slider";
import Wrapper from "../../../components/helpers/Wrapper"
import PageHeader from "../../../components/layout/pageHeader"
import SelectedGlobalFilter from "../../../components/ui/SelectedGlobalFilter"
import WrapperCard from "../../../components/ui/card/card";
import useApi from "../../../hooks/useApi";
import Card from "../../../components/ui/Card";
import { createErrorLoaderSection, createLoaderSection, createNoDataLoaderSection } from "../../../components/ui/states/States";
import { SwiperSlide } from "swiper/react";
import NoDataAvailableChecker from "../../../utils/NoDataAvailableChecker";
import ErrorBoundary from "../../../utils/ErrorBoundary";
import Dropdown from "../../../components/ui/dropdown/Dropdown";
import ExportDropdown from "../../../components/ui/dropdown/ExportDropdown";
import Pie from "../../../components/ui/charts/Pie"
import BarChart from "../../../components/ui/charts/BarChart";
import useFetch from "../../../hooks/useFetch";
import { getUniqueValuesFromArrofObj } from "../../../utils/Utils";
import Loader from "../../../components/ui/loaders/Loader";
import Datatable from "../../../components/ui/tables/Datatable";
import Button from "../../../components/ui/Button";
import StackedColumnChart from "../../../components/ui/charts/StackedColumnChart";
import CombinedBarLineMultipleAxesChart from "../../../components/ui/charts/CombinedBarLineMultipleAxesChart";
import SortOrderButton from "../../../components/ui/dropdown/SortOrderButton";
import CustomBarChart from "../../../components/ui/charts/CustomBarChart";
import HeatRiskGrowth from "../../../components/ui/charts/HeatRiskGrowth";
// import SortOrderButton from "../../../components/ui/dropdown/SortOrderButton";
import { ReactComponent as TableIcon } from "../../../assets/icons/icons-svg/Table-icon.svg";
import { ReactComponent as ChartIcon } from "../../../assets/icons/icons-svg/Chart-icon.svg";
import SummaryDatatable from "../../../components/ui/tables/SummaryDatatable";
import CompareBarLineChart from "../../../components/ui/charts/CompareBarLineChart";
import CompareAnalysis from "../../../components/compare/CompareAnalysis";
import MapComponent from '../../../pageComponents/MapComponent';


const filterKeyMap = {
    country: "country_id",
    store_type: "store_type_id",
    category: "category_id",
    sub_category: "sub_category_id",
    segment: "segment_id",
    form: "form_id",
    manufacture: "manufacture_id",
    brand: "brand_id",
    variant: "variant_id",
    pack_size: "pack_size_id",
    promotion: "promotion_id",
    sku: "sku_id",
};


const SummaryTable = () => {
    const api_version = "-v1";

    // Pagination and search config
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });

    // Table data structure
    const [rawTableData, setRawTableData] = useState({
        results: [],
        label: [],
        page: {}
    });

    const { post, isLoading, error } = useFetch();

    // --- Date filters: adapt/remove if not needed in your case ---
    // If you use Redux for date pickers, otherwise remove this and set payload as {}
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date || {});

    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Memoized payload for the API call
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {}
        // No pagination here: we want to fetch all for client-side logic
    }), [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters

    ]);

    // Fetch data on mount or when payload changes
    useEffect(() => {
        const fetchData = async () => {
            // Use correct string interpolation!
            const response = await post(`ri/distribution/distribution-summary${api_version}`, payload);

            if (response) {
                const { data = [], label = [], pagination = {} } = response;
                setRawTableData({
                    results: data,
                    label,
                    page: pagination
                });
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [payload]);

    // --- Client-side: filter, sort, paginate ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Handler for Datatable pagination/search/sort
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    let tableContent = null;
    if (isLoading) {
        tableContent = <Loader loaderType="smallTableLoader" />;
    } else if (error) {
        tableContent = createErrorLoaderSection('smallTableLoader');
    } else if (!isLoading && (!rawTableData || rawTableData.results.length === 0 || NoDataAvailableChecker(rawTableData.results))) {
        tableContent = createNoDataLoaderSection('smallTableLoader');
    } else {
        tableContent = (
            <SummaryDatatable
                tableTitle={"Summary"}
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                showPagination={true}
                searchPlaceHolder="Search here..."
                // showGrowthDegrowth={true}   // Uncomment if needed
                isFooterShow={true}
                hideBoxShadow={true}
                rawTableData={filtered}
            />
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {tableContent}
            </ErrorBoundary>
        </Wrapper>
    );
};


const DistributionEfficiencyPerformance = () => {
    const api_version = "-v1";
    const granularityOptions = [
        { label: "Yearly", value: "year" },
        { label: "Quarterly", value: "quarter_no" },
        { label: "Monthly", value: "month_no" }
    ];
    // Granularity dropdown
    const [selectedGranularity, setSelectedGranularity] = useState(granularityOptions[0]);
    const [chart, setChart] = useState({ chartData: [], chartLabels: [] });

    // Table logic
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });
    const [sortOrder, setSortOrder] = useState("desc");
    const [toggleTable, setToggleTable] = useState(false);

    const { post, isLoading, error } = useFetch();

    // Redux date state
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Memoized payload
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            granularity_dropdown: selectedGranularity.value,
            // top_bottom: { sort_by: sortOrder, size: 5 }
        }
    }), [
        selectedGranularity,
        sortOrder,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // Toggle sort order
    const toggleSortOrder = () => {
        const newOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newOrder
        }));
    };

    // Fetch table data
    const fetchTableData = async () => {
        const response = await post(
            `/ri/distribution/distribution-distribution-efficiency-performance-table${api_version}`,
            payload
        );
        setRawTableData({
            results: response.data || [],
            label: response.label || [],
            page: response.pagination || {}
        });
    };

    // Transform API data to chartData/chartLabels for CombinedBarLineMultipleAxesChart
    const transformChartData = (apiResponse) => {
        if (!apiResponse || !apiResponse.data || !apiResponse.label || !apiResponse.label.length) {
            return { chartData: [], chartLabels: [] };
        }
        // X axis labels
        const chartData = apiResponse.data.map(row => ({
            ...row,
            label: (row.label || "").trim()
        }));

        // Use labels from API response for bar/line series
        const chartLabels = apiResponse.label.map(l => ({
            barchartLabels: l.barchartLabels,
            linechartLabels: l.linechartLabels
        }));

        return { chartData, chartLabels };
    };

    // Fetch chart data and transform for chart component
    const fetchChartData = async () => {
        const response = await post(
            `/ri/distribution/distribution-distribution-efficiency-performance${api_version}`,
            payload
        );
        setChart(transformChartData(response));
    };

    useEffect(() => {
        toggleTable ? fetchTableData() : fetchChartData();
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Utility loaders
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart.chartData.length || NoDataAvailableChecker(chart.chartData)) return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <CombinedBarLineMultipleAxesChart
                    chartClass="section-chart-wrapper"
                    chartData={chart.chartData}
                    chartLabels={chart.chartLabels}
                    chartId={`distributionEfficiencyPerformanceChart-${Math.random()}`}
                    showLabels
                    showLegend
                    tooltipMaxWidth="auto"
                    legendType="api"
                />
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="Distribution Efficiency Performance"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                hideBoxShadow={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <ErrorBoundary>
            <div id="distribution_efficiency_performance_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">Distribution Efficiency Performance</h2>
                        <div data-html2canvas-ignore>
                            <Dropdown
                                placeholder="Granularity"
                                options={granularityOptions}
                                value={selectedGranularity}
                                selectedOptions={selectedGranularity}
                                setStatedropdown={setSelectedGranularity}
                                className="grid_margin_right"
                            />
                            <span className="custom-tooltip-container">
                                <button
                                    type="button"
                                    className="icon-toggle-btn"
                                    onClick={() => setToggleTable(prev => !prev)}
                                    aria-label={toggleTable ? "View Chart" : "View Table"}
                                >
                                    {toggleTable ? (
                                        <ChartIcon />
                                    ) : (
                                        <TableIcon />
                                    )}
                                </button>
                                <span className="custom-tooltip">
                                    {toggleTable ? "View Chart" : "View Table"}
                                </span>
                            </span>
                            <ExportDropdown
                                sectionId="distribution_efficiency_performance_section"
                                sectionName="Distribution Efficiency Performance"
                                sectionData={toggleTable ? filtered : chart.chartData}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {toggleTable ? renderTable() : renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const SegmentWiseDistributionEfficiency = () => {
    const api_version = '-v1';

    // Dropdown options
    const granularityOptions = [
        { label: "Yearly", value: "year" },
        { label: "Quarterly", value: "quarter_no" },
        { label: "Monthly", value: "month_no" },
    ];
    const dimensionOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" }
    ];

    // State
    const [granularity, setGranularity] = useState(granularityOptions[0]);
    const [dimension, setDimension] = useState(dimensionOptions[0]);
    const [sortOrder, setSortOrder] = useState("desc");
    const [toggleTable, setToggleTable] = useState(false);

    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    // Chart & Table state
    const [chart, setChart] = useState({ chartData: [], chartLabels: [] });
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });

    const { post, isLoading, error } = useFetch();
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Payload for chart/table
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            granularity_dropdown: granularity.value,
            dimension_dropdown: dimension.value,
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    }), [
        granularity,
        dimension,
        sortOrder,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // --- Chart transformation ---
    function transformChartData(apiData, labelMeta) {
        if (!Array.isArray(apiData) || !Array.isArray(labelMeta)) return { chartData: [], chartLabels: [] };
        const chartLabels = labelMeta.map(l => ({
            label: l.label,
            value: l.value
        }));
        const brands = labelMeta.map(l => l.value);
        const chartData = apiData.map(row => {
            const transformed = { label: row.label };
            brands.forEach(brand => {
                transformed[brand] = row[brand] ?? 0;
            });
            return transformed;
        });
        return { chartData, chartLabels };
    }

    // Fetch chart or table data
    const fetchTableData = async () => {
        const response = await post(
            `ri/distribution/distribution-segment-wise-table${api_version}`,
            payload
        );
        if (response) {
            const safeData = (response.data || []).map(row => {
                const safeRow = {};
                Object.entries(row).forEach(([k, v]) => {
                    safeRow[k] = v == null ? "" : v;
                });
                return safeRow;
            });
            setRawTableData({
                results: safeData,
                label: response.label || [],
                page: response.pagination || {}
            });
        }
    };

    const fetchChartData = async () => {
        const response = await post(
            `ri/distribution/distribution-segment-wise${api_version}`,
            payload
        );
        let chartData = [];
        let chartLabels = [];
        if (response && response.data && response.label) {
            const transformed = transformChartData(response.data, response.label);
            chartData = transformed.chartData;
            chartLabels = transformed.chartLabels;
        }
        setChart({ chartData, chartLabels });
    };

    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Utility functions
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );

    // Pagination/search/sort handler for table (client-side)
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Table and chart renderers
    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart || !chart.chartData || chart.chartData.length === 0 || NoDataAvailableChecker(chart.chartData))
            return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <div style={{ minHeight: 320, height: "340px", position: 'relative' }}>
                    <BarChart
                        chartClass="section-chart-wrapper"
                        chartData={chart.chartData}
                        chartLabels={chart.chartLabels}
                        chartId={'stackedColumnChart-' + Math.random() * 1000}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                        isHorizontal={false}
                        variant="centeredLegend"
                    />
                </div>
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        if (error) return createErrorLoaderSection('smallTableLoader');
        if (!filtered.length || NoDataAvailableChecker(filtered))
            return createNoDataLoaderSection('smallTableLoader');
        return (
            <Datatable
                tableTitle="Distribution Effectiveness"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                showPagination={true}
                hideBoxShadow={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    // Main UI
    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="segment_distribution_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Segment Distribution Effectiveness</h2>
                            <div data-html2canvas-ignore>
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionOptions}
                                    className="grid_margin_right"
                                    value={dimension}
                                    selectedOptions={dimension}
                                    setStatedropdown={setDimension}
                                />
                                <Dropdown
                                    placeholder="Granularity"
                                    options={granularityOptions}
                                    className="grid_margin_right"
                                    value={granularity}
                                    selectedOptions={granularity}
                                    setStatedropdown={setGranularity}
                                />
                                <Button
                                    type="primary"
                                    className="width_fit_content grid_margin_right"
                                    handleClick={toggleSortOrder}
                                >
                                    {sortOrder === "desc" ? "Top" : "Bottom"}
                                </Button>
                                <Button
                                    type="primary"
                                    className="width_fit_content"
                                    handleClick={() => setToggleTable(prev => !prev)}
                                >
                                    {toggleTable ? "Chart" : "Table"}
                                </Button>
                                <ExportDropdown
                                    sectionId="segment_distribution"
                                    sectionName="segment_distribution"
                                    sectionData={toggleTable ? filtered : chart.chartData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? renderTable() : renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const DistributionEffectiveness = () => {
    const api_version = '-v1';

    // Dropdown options
    const granularityOptions = [
        { label: "Yearly", value: "year" },
        { label: "Quarterly", value: "quarter_no" },
        { label: "Monthly", value: "month_no" },
    ];
    const dimensionOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" }
    ];

    // State
    const [granularity, setGranularity] = useState(granularityOptions[0]);
    const [dimension, setDimension] = useState(dimensionOptions[0]);
    const [sortOrder, setSortOrder] = useState("desc");
    const [toggleTable, setToggleTable] = useState(false);

    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    // Chart & Table state
    const [chart, setChart] = useState({ chartData: [], chartLabels: [] });
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });

    const { post, isLoading, error } = useFetch();
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Payload for chart/table
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            granularity_dropdown: granularity.value,
            dimension_dropdown: dimension.value,
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    }), [
        granularity,
        dimension,
        sortOrder,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // --- Chart transformation ---
    function transformChartData(apiData, labelMeta) {
        if (!Array.isArray(apiData) || !Array.isArray(labelMeta)) return { chartData: [], chartLabels: [] };
        const chartLabels = labelMeta.map(l => ({
            label: l.label,
            value: l.value
        }));
        const brands = labelMeta.map(l => l.value);
        const chartData = apiData.map(row => {
            const transformed = { label: row.label };
            brands.forEach(brand => {
                transformed[brand] = row[brand] ?? 0;
            });
            return transformed;
        });
        return { chartData, chartLabels };
    }

    // Fetch chart or table data
    const fetchTableData = async () => {
        const response = await post(
            `ri/distribution/distribution-distribution-effectiveness-table${api_version}`,
            payload
        );
        if (response) {
            const safeData = (response.data || []).map(row => {
                const safeRow = {};
                Object.entries(row).forEach(([k, v]) => {
                    safeRow[k] = v === null || v === undefined ? "" : v;
                });
                return safeRow;
            });
            setRawTableData({
                results: safeData,
                label: response.label || [],
                page: response.pagination || {}
            });
        }
    };

    const fetchChartData = async () => {
        const response = await post(
            `ri/distribution/distribution-distribution-effectiveness${api_version}`,
            payload
        );
        let chartData = [];
        let chartLabels = [];
        if (response && response.data && response.label) {
            const transformed = transformChartData(response.data, response.label);
            chartData = transformed.chartData;
            chartLabels = transformed.chartLabels;
        }
        setChart({ chartData, chartLabels });
    };

    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Utility functions
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );

    // Pagination/search/sort handler for table (client-side)
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Table and chart renderers
    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        // if (error) return createErrorLoaderSection('stackedColumnChart');
        // if (!chart || !chart.chartData || chart.chartData.length === 0 || NoDataAvailableChecker(chart.chartData))
        //     return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <div style={{ minHeight: 320, height: "340px", position: 'relative' }}>
                    <BarChart
                        chartClass="section-chart-wrapper"
                        chartData={chart.chartData}
                        chartLabels={chart.chartLabels}
                        chartId={'stackedColumnChart-' + Math.random() * 1000}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                        isHorizontal={false}
                        variant="centeredLegend"
                    />
                </div>
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="Distribution Effectiveness"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    // Main UI
    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="distribution_effectiveness_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Distribution Effectiveness</h2>
                            <div data-html2canvas-ignore>
                                {/* <Dropdown
                                    placeholder="Granularity"
                                    options={granularityOptions}
                                    className="grid_margin_right"
                                    value={granularity}
                                    selectedOptions={granularity}
                                    setStatedropdown={setGranularity}
                                /> */}
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionOptions}
                                    className="grid_margin_right"
                                    value={dimension}
                                    selectedOptions={dimension}
                                    setStatedropdown={setDimension}
                                />
                                <SortOrderButton
                                    sortOrder={sortOrder}
                                    toggleSortOrder={toggleSortOrder}
                                    className="grid_margin_right"
                                />
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable(prev => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                    >
                                        {toggleTable ? (
                                            <ChartIcon />
                                        ) : (
                                            <TableIcon />
                                        )}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown
                                    sectionId="distribution_effectiveness_section"
                                    sectionName="distribution effectiveness"
                                    sectionData={toggleTable ? filtered : chart.chartData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? renderTable() : renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const DistributionImpactAnalysis = () => {
    const api_version = "-v1";
    const [toggleTable, setToggleTable] = useState(false);
    const [toggleTopBottom, setToggleTopBottom] = useState(true);

    // Table states
    const [rawTableData, setRawTableData] = useState({ results: [], headers: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    // Chart states
    const [chart, setChart] = useState({
        data: [],
        label: []
    });

    const { post, isLoading, error } = useFetch();

    const dimensionDropdownOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" },
    ];
    const [dimensionDropdown, setDimensionDropdown] = useState(dimensionDropdownOptions[7]);
    const [sortOrder, setSortOrder] = useState("desc");

    // Redux date state
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Derive sortOrder from toggleTopBottom
    useEffect(() => {
        const newOrder = toggleTopBottom ? "desc" : "asc";
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newOrder
        }));
    }, [toggleTopBottom]);

    // Memoized payload
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            dimension_dropdown: dimensionDropdown.value,
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    }), [
        sortOrder,
        dimensionDropdown,
        paginationConfig.order_type,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Fetch table and chart data
    useEffect(() => {
        const fetchData = async () => {
            if (!toggleTable) {
                // Chart
                const response = await post(
                    `/ri/distribution/distribution-impact-analysis${api_version}`,
                    payload
                );
                setChart(response || { data: [], label: [] });
            } else {
                // Table
                const response = await post(
                    `/ri/distribution/distribution-impact-analysis-table${api_version}`,
                    payload
                );
                setRawTableData({
                    results: response?.data || [],
                    headers: response?.label || [],
                    page: response?.pagination || {}
                });
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || rawTableData.page.per_page || 10;
    const pageNum = paginationConfig.page_num || rawTableData.page.current_page || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Utility loaders
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const renderChart = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart.data.length || NoDataAvailableChecker(chart.data)) return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <CombinedBarLineMultipleAxesChart
                    chartClass="section-chart-wrapper"
                    chartData={chart.data}
                    chartLabels={chart.label}
                    chartId={`distributionImpactChart-${Math.random()}`}
                    showLabels
                    showLegend
                    tooltipMaxWidth="auto"
                    legendType="api"
                />
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="Distribution Impact Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.headers}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                hideBoxShadow={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <ErrorBoundary>
            <div id="distribution_impact_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">Distribution Impact Analysis</h2>
                        <div data-html2canvas-ignore>
                            <Dropdown
                                placeholder="Granularity"
                                options={dimensionDropdownOptions}
                                className="grid_margin_right"
                                value={dimensionDropdown}
                                selectedOptions={dimensionDropdown}
                                setStatedropdown={setDimensionDropdown}
                            />
                            <SortOrderButton
                                sortOrder={sortOrder}
                                toggleSortOrder={toggleSortOrder}
                                className="grid_margin_right"
                            />
                            <span className="custom-tooltip-container">
                                <button
                                    type="button"
                                    className="icon-toggle-btn"
                                    onClick={() => setToggleTable(prev => !prev)}
                                    aria-label={toggleTable ? "View Chart" : "View Table"}
                                >
                                    {toggleTable ? (
                                        <ChartIcon />
                                    ) : (
                                        <TableIcon />
                                    )}
                                </button>
                                <span className="custom-tooltip">
                                    {toggleTable ? "View Chart" : "View Table"}
                                </span>
                            </span>
                            <ExportDropdown
                                sectionId="distribution_impact_analysis_section"
                                sectionName="Distribution Impact Analysis"
                                sectionData={toggleTable ? filtered : chart.data}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {toggleTable ? renderTable() : renderChart()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const TDPTrendAnalysis = () => {
    const api_version = "-v1";

    const valueDropdownOptions = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Unit", value: "sales_unit" },
    ];

    const [toggleTable, setToggleTable] = useState(false);
    const [valueDropdown, setValueDropdown] = useState(valueDropdownOptions[0]);

    // Redux date state
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Payload state
    const [payload, setPayload] = useState({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate || "2023-01-01",
                mat_ty_end_date: endDate || "2023-12-31",
                mat_ly_start_date: prevStartDate || "2022-01-01",
                mat_ly_end_date: prevEndDate || "2022-12-31",
                mat_2y_start_date: secondPrevStartDate || "2021-01-01",
                mat_2y_end_date: secondPrevEndDate || "2021-12-31"
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            value_dropdown: valueDropdown.value,
        }
    });

    // Update payload when date/global filter/valueDropdown changes
    useEffect(() => {
        setPayload(prev => ({
            ...prev,
            global_filter: {
                ...prev.global_filter,
                date_filter: {
                    mat_ty_start_date: startDate || "2023-01-01",
                    mat_ty_end_date: endDate || "2023-12-31",
                    mat_ly_start_date: prevStartDate || "2022-01-01",
                    mat_ly_end_date: prevEndDate || "2022-12-31",
                    mat_2y_start_date: secondPrevStartDate || "2021-01-01",
                    mat_2y_end_date: secondPrevEndDate || "2021-12-31"
                },
                filter: apiGlobalFilters
            },
            section_filter: {
                ...prev.section_filter,
                value_dropdown: valueDropdown.value,
            }
        }));
        setChart(prev => ({
            ...prev,
            chartLabels: [{
                barchartLabels: [{ label: "Weighted Distribution Points (TDP)", value: "tdp" }],
                linechartLabels: [{ label: valueDropdown.label, value: valueDropdown.value }]
            }]
        }));
        // eslint-disable-next-line
    }, [
        valueDropdown,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const [chart, setChart] = useState({
        chartData: [],
        chartLabels: [{
            barchartLabels: [{ label: "Weighted Distribution Points (TDP)", value: "tdp" }],
            linechartLabels: [{ label: valueDropdown.label, value: valueDropdown.value }]
        }]
    });

    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    const { post, isLoading, error } = useFetch();

    const fetchTableData = async () => {
        const response = await post(`/ri/distribution/distribution-tdp-analysis-table${api_version}`, payload);
        const results = response.data || [];
        const label = response.label || [];
        const page = response.pagination || {};
        setRawTableData({ results, label, page });
    };

    const fetchChartData = async () => {
        const response = await post(`/ri/distribution/distribution-tdp-analysis${api_version}`, payload);
        setChart({
            chartData: response.data || [],
            chartLabels: response.label || chart.chartLabels
        });
    };

    useEffect(() => {
        toggleTable ? fetchTableData() : fetchChartData();
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const renderChart = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart.chartData.length || NoDataAvailableChecker(chart.chartData)) return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <CombinedBarLineMultipleAxesChart
                    chartClass="section-chart-wrapper"
                    chartData={chart.chartData}
                    chartLabels={chart.chartLabels}
                    chartId={`tdpTrendChart-${Math.random()}`}
                    showLabels
                    showLegend
                    legendType="tdp"
                    tooltipMaxWidth="auto"
                />
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="TDP Trend Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search TDP trends..."
                showPagination={true}
                hideBoxShadow={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <ErrorBoundary>
            <div id="trend_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">TDP Trend Analysis</h2>
                        <div data-html2canvas-ignore>
                            <Dropdown
                                placeholder="Value"
                                options={valueDropdownOptions}
                                value={valueDropdown}
                                selectedOptions={valueDropdown}
                                setStatedropdown={setValueDropdown}
                                className="grid_margin_right"
                            />
                            <span className="custom-tooltip-container">
                                <button
                                    type="button"
                                    className="icon-toggle-btn"
                                    onClick={() => setToggleTable(prev => !prev)}
                                    aria-label={toggleTable ? "View Chart" : "View Table"}
                                >
                                    {toggleTable ? (
                                        <ChartIcon />
                                    ) : (
                                        <TableIcon />
                                    )}
                                </button>
                                <span className="custom-tooltip">
                                    {toggleTable ? "View Chart" : "View Table"}
                                </span>
                            </span>
                            <ExportDropdown sectionId="trend_analysis_section" sectionName="TDP Trend Analysis" sectionData={toggleTable ? filtered : chart.chartData} />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {toggleTable ? renderTable() : renderChart()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const DistributionOverview = () => {
    const api_version = "-v1";
    const apiEndpoints = [
        { key: "promotion", url: `ri/distribution/distribution-distribution-overview-pie-chart-promotion${api_version}`, chartType: "Pie" },
        { key: "category", url: `ri/distribution/distribution-distribution-overview-pie-chart-category${api_version}`, chartType: "Pie" },
        { key: "segment", url: `ri/distribution/distribution-distribution-overview-bar-chart-segment${api_version}`, chartType: "Bar" },
        { key: "manufacturer", url: `ri/distribution/distribution-distribution-overview-bar-chart-manufacturer${api_version}`, chartType: "Bar" },
        { key: "brand", url: `ri/distribution/distribution-distribution-overview-bar-chart-brand${api_version}`, chartType: "Bar" },
        { key: "variant", url: `ri/distribution/distribution-distribution-overview-bar-chart-variant${api_version}`, chartType: "Bar" },
        { key: "pack Size", url: `ri/distribution/distribution-distribution-overview-bar-chart-pack-size${api_version}`, chartType: "Bar" },
    ];

    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Always use TDP Value
    const TDP_LABEL = "TDP Value";
    const TDP_VALUE = "tdp_value"; // Confirm this matches your backend

    const [sortOrder, setSortOrder] = useState("desc");

    const [payload, setPayload] = useState({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            value_dropdown: "tdp",
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    });

    // Update payload when date/global filter/sortOrder changes
    useEffect(() => {
        setPayload(prevPayload => ({
            ...prevPayload,
            global_filter: {
                ...prevPayload.global_filter,
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            },
            section_filter: {
                ...prevPayload.section_filter,
                top_bottom: {
                    ...prevPayload.section_filter.top_bottom,
                    sort_by: sortOrder
                }
            }
        }));
        // eslint-disable-next-line
    }, [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters,
        sortOrder
    ]);

    // Update sort order and payload
    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPayload((prevPayload) => ({
            ...prevPayload,
            section_filter: {
                ...prevPayload.section_filter,
                top_bottom: {
                    ...prevPayload.section_filter.top_bottom,
                    sort_by: newSortOrder
                }
            }
        }));
    };

    const ChartCard = ({ endpoint }) => {
        const { key, url, chartType } = endpoint;
        const [data, setData] = useState(null);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState(null);
        const { post } = useFetch();

        useEffect(() => {
            setIsLoading(true);
            post(url, payload)
                .then(response => {
                    setData(response?.data || response);
                    setError(null);
                })
                .catch(err => {
                    setError(err);
                    setData(null);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }, [url, payload]);

        return (
            <WrapperCard className="p_10" style={{
                maxHeight: "300px",
                paddingBottom: "16px",
                border: "1px solid #eee",
                borderRadius: "12px",
                background: "#fff"
            }}>
                <div className="header">
                    <h5 className="card_title">
                        <b>{key.charAt(0).toUpperCase() + key.slice(1)}</b>
                        <span className="info_icon"></span>
                    </h5>
                </div>
                {isLoading ? (
                    createLoaderSection("chartCard")
                ) : error ? (
                    createErrorLoaderSection("chartCard")
                ) : !data || data.length === 0 ? (
                    createNoDataLoaderSection("chartCard")
                ) : chartType === "Pie" ? (
                    <Pie
                        chartData={data}
                        chartId={`${key}-chart-${Math.floor(Math.random() * 1000)}`}
                        chartClass="section-chart-wrapper"
                        legendLabel={TDP_LABEL}
                    />
                ) : (
                    <BarChart
                        chartData={data}
                        chartLabels={[{ label: TDP_LABEL, value: "value" }]}
                        chartId={`${key}-chart-${Math.floor(Math.random() * 1000)}`}
                        chartClass="section-chart-wrapper"
                        legendLabel={TDP_LABEL}
                    />
                )}
            </WrapperCard>
        );
    };

    return (
        <Wrapper>
            <WrapperCard>
                <WrapperCard.HeaderWrap>
                    <WrapperCard.Header>
                        <WrapperCard.Header.Title>Distribution Overview</WrapperCard.Header.Title>
                    </WrapperCard.Header>
                    <div data-html2canvas-ignore={true}>
                        <SortOrderButton
                            sortOrder={sortOrder}
                            toggleSortOrder={toggleSortOrder}
                            className="sort-order-button"
                        />
                    </div>
                </WrapperCard.HeaderWrap>
                <br />
                <WrapperCard.Body>
                    <Slider>
                        {apiEndpoints.map((endpoint) => (
                            <SwiperSlide key={endpoint.key}>
                                <ChartCard endpoint={endpoint} />
                            </SwiperSlide>
                        ))}
                    </Slider>
                </WrapperCard.Body>
            </WrapperCard>
        </Wrapper>
    );
};

const DistributionPerformance = () => {
    const api_version = "-v1";

    // 2. Get date values from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Use those dates and global filters in your payload, and store in state
    const [payload, setPayload] = useState({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        }
        // section_filter: {
        //     value_dropdown: "sales_unit",
        //     top_bottom: {
        //         sort_by: "desc",
        //         size: 5
        //     }
        // }
    });

    // 4. Update payload when Redux date values or global filters change
    useEffect(() => {
        setPayload(prev => ({
            ...prev,
            global_filter: {
                ...prev.global_filter,
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            }
        }));
        // eslint-disable-next-line
    }, [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const { post } = useFetch();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // 5. Fetch data whenever payload changes
    useEffect(() => {
        setIsLoading(true);
        setError(null);

        post(`ri/distribution/distribution-distribution-performance${api_version}`, payload)
            .then(response => {
                setData(response?.data || response);
                setError(null);
            })
            .catch(err => {
                setError(err);
                setData(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [payload, post, api_version]);

    let content;

    if (isLoading) {
        content = createLoaderSection('cardLoader', "Distribution Performance", 'chartCard');
    } else if (error) {
        content = createErrorLoaderSection('cardType');
    } else if (!isLoading && (!data || data.length === 0 || NoDataAvailableChecker(data))) {
        content = createNoDataLoaderSection('cardType');
    } else {
        const CARD_DATA = data || [];
        content = (
            <Slider sliderTitle="Distribution Performance">
                {CARD_DATA.map((data, index) => (
                    <SwiperSlide key={index}>
                        <Card
                            module="summary"
                            section="summary_distribution_performance"
                            variant={"chartCard"}
                            cardTitle={data.label}
                            currentValue={data.currentValue}
                            previousValue={data.previousValue}
                            chartType={"Line"}
                            chartData={data.subData?.data}
                            chartDataLabels={data.subData?.dimension}
                        />
                    </SwiperSlide>
                ))}
            </Slider>
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {content}
            </ErrorBoundary>
        </Wrapper>
    );
};

const DistributionEfficiency = () => {
    const api_version = "-v1";
    // Redux selectors for dates
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Chart & Table state
    const [chart, setChart] = useState({ chartData: [], chartLabels: [] });
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });

    // Table/Chart toggles and sort order
    const [toggleTable, setToggleTable] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    const { post, isLoading, error } = useFetch();

    // Payload for chart/table
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        pagination: {
            page_num: paginationConfig.page_num,
            page_size: paginationConfig.page_size,
            sort: [
                {
                    sort_column: "distribution_efficiency",
                    sort_by: sortOrder
                }
            ]
        }
    }), [
        startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate,
        paginationConfig.page_num, paginationConfig.page_size, sortOrder, apiGlobalFilters
    ]);

    // Transform chart data logic (adjust as per your API response)
    function transformChartData(apiData, labelMeta) {
        if (!Array.isArray(apiData) || !Array.isArray(labelMeta)) return { chartData: [], chartLabels: [] };
        const brands = labelMeta.map(l => l.value);
        const periods = apiData.map(row => row.label);
        const chartLabels = brands.map((brand, i) => ({
            label: labelMeta.find(l => l.value === brand)?.label || brand,
            value: `${brand}_distribution_efficiency`
        }));
        const chartData = periods.map((period, periodIdx) => {
            const row = { label: period };
            brands.forEach(brand => {
                row[`${brand}_distribution_efficiency`] = apiData[periodIdx][`${brand}_distribution_efficiency`] ?? 0;
                row[`${brand}_sales_value`] = apiData[periodIdx][`${brand}_sales_value`] ?? 0;
            });
            return row;
        });
        return { chartData, chartLabels };
    }

    // Fetch chart or table data
    const fetchTableData = async () => {
        const response = await post(
            `ri/distribution/distribution-efficiency-table${api_version}`,
            payload
        );
        if (response) {
            const safeData = (response.data || []).map(row => {
                const safeRow = {};
                Object.entries(row).forEach(([k, v]) => {
                    safeRow[k] = v === null || v === undefined ? "" : v;
                });
                return safeRow;
            });
            setRawTableData({
                results: safeData,
                label: response.label || [],
                page: response.pagination || {}
            });
        }
    };

    const fetchChartData = async () => {
        const response = await post(
            `ri/distribution/distribution-efficiency${api_version}`,
            payload
        );
        let chartData = [];
        let chartLabels = [];
        if (response && response.data && response.label) {
            const transformed = transformChartData(response.data, response.label);
            chartData = transformed.chartData;
            chartLabels = transformed.chartLabels;
        }
        setChart({ chartData, chartLabels });
    };

    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable, api_version, post]);

    // Table logic
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    // Pagination/search/sort handler for table (client-side)
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart || !chart.chartData || chart.chartData.length === 0 || NoDataAvailableChecker(chart.chartData))
            return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <CustomBarChart
                    chartClass="section-chart-wrapper"
                    chartData={chart.chartData}
                    chartLabels={chart.chartLabels}
                    chartId={'stackedColumnChart-' + Math.random() * 1000}
                    showLabels
                    showLegend
                    tooltipMaxWidth="auto"
                    dimensionField="period"
                    dimensionLabel="Period"
                />
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        if (error) return createErrorLoaderSection('smallTableLoader');
        if (!filtered.length || NoDataAvailableChecker(filtered))
            return createNoDataLoaderSection('smallTableLoader');
        return (
            <Datatable
                tableTitle="Distribution Efficiency"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                showPagination={true}
                searchPlaceHolder="Search here..."
            />
        );
    };

    return (
        <ErrorBoundary>
            <div id="distribution_efficiency_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">Distribution Efficiency</h2>
                        <div data-html2canvas-ignore>
                            <Button
                                type="primary"
                                className="width_fit_content grid_margin_right"
                                handleClick={toggleSortOrder}
                            >
                                {sortOrder === "desc" ? "Top" : "Bottom"}
                            </Button>
                            <span className="custom-tooltip-container">
                                <button
                                    type="button"
                                    className="icon-toggle-btn"
                                    onClick={() => setToggleTable(prev => !prev)}
                                    aria-label={toggleTable ? "View Chart" : "View Table"}
                                >
                                    {toggleTable ? (
                                        <ChartIcon />
                                    ) : (
                                        <TableIcon />
                                    )}
                                </button>
                                <span className="custom-tooltip">
                                    {toggleTable ? "View Chart" : "View Table"}
                                </span>
                            </span>
                            <ExportDropdown
                                sectionId="distribution_efficiency"
                                sectionName="distribution_efficiency"
                                sectionData={toggleTable ? filtered : chart.chartData}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {toggleTable ? renderTable() : renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const GrowthComparisonAnalysis = () => {
    // Redux date state

    const dimensionDropdown = [
        { label: "Segment", value: "segment" },
        { label: "Category", value: "category" },
        { label: "Brand", value: "brand" },
        { label: "Manufacture", value: "manufacture" }
        // Add more as needed
    ];
    const valueDropdown = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Unit", value: "sales_unit" }
    ];
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    const api_version = "-v1";
    const [dimensionFilter, setDimensionFilter] = useState(dimensionDropdown[0]);
    const [valueFilter, setValueFilter] = useState(valueDropdown[0]);

    // Chart state
    const [matrixData, setMatrixData] = useState([]);
    const [xLabels, setXLabels] = useState([]);
    const [yLabels, setYLabels] = useState([]);
    const { post, isLoading, error } = useFetch();

    // Memoized payload
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            dimension: dimensionFilter.value,
            value: valueFilter.value
        }
    }), [
        startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate,
        dimensionFilter, valueFilter, apiGlobalFilters
    ]);

    const fetchMatrixData = async () => {
        const response = await post(
            `ri/distribution/distribution-growth-compare-analysis${api_version}`,
            payload
        );
        if (response) {
            // Assume response.data is a matrix array, with xLabels (brands), yLabels (segments), matrixData (cell values)
            setMatrixData(response.data?.matrix || []);
            setXLabels(response.data?.x_labels || []);
            setYLabels(response.data?.y_labels || []);
        } else {
            setMatrixData([]);
            setXLabels([]);
            setYLabels([]);
        }
    };

    useEffect(() => {
        fetchMatrixData();
        // eslint-disable-next-line
    }, [payload]);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="matrixChartLoader" />;
        if (error) return createErrorLoaderSection('matrixChart');
        if (!matrixData.length || NoDataAvailableChecker(matrixData)) return createNoDataLoaderSection('matrixChart');
        return (
            <ErrorBoundary>
                <HeatRiskGrowth
                    data={matrixData}
                    xLabels={xLabels}
                    yLabels={yLabels}
                    valueField={valueFilter.value}
                    dimensionField={dimensionFilter.value}
                />
            </ErrorBoundary>
        );
    };

    return (
        <ErrorBoundary>
            <div id="growth_comparison_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">Growth Comparison Analysis</h2>
                        <div data-html2canvas-ignore>
                            <Dropdown
                                placeholder="Dimension"
                                options={dimensionDropdown}
                                className="grid_margin_right"
                                value={dimensionFilter}
                                selectedOptions={dimensionFilter}
                                setStatedropdown={setDimensionFilter}
                            />
                            <Dropdown
                                placeholder="Value"
                                options={valueDropdown}
                                className="grid_margin_right"
                                value={valueFilter}
                                selectedOptions={valueFilter}
                                setStatedropdown={setValueFilter}
                            />
                            <ExportDropdown
                                sectionId="growth_comparison_analysis_section"
                                sectionName="Growth Comparison Analysis"
                                sectionData={matrixData}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const DistributionPriceImpactAnalysis = () => {
    const api_version = "-v1";
    const dimensionOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" }
    ];
    const [toggleTable, setToggleTable] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");

    const [rawTableData, setRawTableData] = useState({
        results: [],
        label: [],
        page: {}
    });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });
    const [chart, setChart] = useState({ data: [], label: [] });
    const { post, isLoading, error } = useFetch();
    const [selectedDimension, setSelectedDimension] = useState(dimensionOptions[7]);

    // Redux date state
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Memoized payload
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            dimension_dropdown: selectedDimension.value,
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    }), [
        selectedDimension,
        sortOrder,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // Clean data utility
    const cleanData = (data) => {
        return data.map(item => {
            const cleanedItem = {};
            Object.keys(item).forEach(key => {
                cleanedItem[key] = item[key] === null || item[key] === undefined ? '' : item[key];
            });
            return cleanedItem;
        });
    };

    // Toggle sort order
    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Fetch table data
    const fetchTableData = async () => {
        try {
            const response = await post(`ri/distribution/distribution-price-impact-analysis-table${api_version}`, payload);
            if (response) {
                const { data = [], label = [], pagination = {} } = response;
                setRawTableData({
                    results: cleanData(data),
                    label,
                    page: pagination
                });
            }
        } catch (err) {
            setRawTableData({ results: [], label: [], page: {} });
        }
    };

    // Fetch chart data
    const fetchChartData = async () => {
        try {
            const response = await post(`ri/distribution/distribution-price-impact-analysis${api_version}`, payload);
            setChart(response);
        } catch (err) {
            setChart({ data: [], label: [] });
        }
    };

    // Effect for table/chart toggle
    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // Client-side: filter, sort, paginate table data
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Loaders
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    // Chart options: same as your previous file
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: false,
                grid: { display: false }
            },
            y: {
                stacked: false,
                beginAtZero: true,
                min: 0,
                ticks: {
                    callback: function (value) {
                        return value + '%';
                    }
                },
                grid: {
                    drawBorder: false,
                    color: function (context) {
                        return context.tick.value === 0 ? '#666' : '#eee';
                    },
                    lineWidth: function (context) {
                        return context.tick.value === 0 ? 2 : 1;
                    }
                }
            }
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    boxWidth: 12,
                    padding: 20,
                    font: { size: 12 }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.raw;
                        const formattedValue = Math.abs(value) > 1000
                            ? value.toLocaleString('en-US', { maximumFractionDigits: 2 })
                            : value.toFixed(2);
                        return `${label}: ${formattedValue}%`;
                    }
                },
                displayColors: true,
                backgroundColor: 'rgba(0,0,0,0.8)',
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
                padding: 10
            }
        },
        elements: {
            bar: {
                backgroundColor: function (context) {
                    const value = context.raw;
                    return value < 0
                        ? 'rgba(255, 99, 132, 0.8)'
                        : 'rgba(54, 162, 235, 0.8)';
                },
                borderColor: function (context) {
                    const value = context.raw;
                    return value < 0
                        ? 'rgba(255, 99, 132, 1)'
                        : 'rgba(54, 162, 235, 1)';
                },
                borderWidth: 1,
                borderRadius: 4,
                minBarLength: 2
            }
        }
    };

    // Transform chart data if needed
    const transformChartData = (data) => {
        if (!data || !data.data) return data;
        return {
            ...data,
            data: data.data.map(item => ({
                ...item,
                percentage_change_in_average_price_per_unit: Number(item.percentage_change_in_average_price_per_unit),
                percentage_change_in_sales_value: Number(item.percentage_change_in_sales_value),
                percentage_change_in_sales_unit: Number(item.percentage_change_in_sales_unit)
            }))
        };
    };

    const chartContent = isLoading ? (
        <Loader loaderType="stackedColumnChartLoader" />
    ) : error ? (
        createErrorLoaderSection("stackedColumnChart")
    ) : !chart?.data?.length || NoDataAvailableChecker(chart) ? (
        createNoDataLoaderSection("stackedColumnChart")
    ) : (
        <Wrapper>
            <ErrorBoundary>
                <div style={{ minHeight: 320, height: "340px", position: 'relative' }}>
                    <BarChart
                        chartClass="section-chart-wrapper"
                        chartData={transformChartData(chart).data}
                        chartLabels={chart.label}
                        chartId={"distributionPriceImpactChart-" + Math.random() * 1000}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                        isHorizontal={false}
                        options={chartOptions}
                        variant="centeredLegend"
                    />
                </div>
            </ErrorBoundary>
        </Wrapper>
    );

    const tableContent = isLoading ? (
        <Loader loaderType="smallTableLoader" />
    ) : error ? (
        createErrorLoaderSection("smallTableLoader")
    ) : !rawTableData?.results?.length || NoDataAvailableChecker(rawTableData.results) ? (
        createNoDataLoaderSection("smallTableLoader")
    ) : (
        <Datatable
            tableTitle="Distribution Price Impact Analysis"
            tableData={paginatedData}
            tableHeader={rawTableData.label}
            tableLength={pageSize}
            tableConfigs={paginationConfig}
            setTableConfigs={handleTableConfigs}
            hideBoxShadow={true}
            tablePaginationData={{
                page_number: pageNum,
                total_pages: totalPages,
                total_records: totalRecords,
                record_end: Math.min(pageNum * pageSize, totalRecords)
            }}
            searchPlaceHolder="Search here..."
            showPagination={true}
        />
    );

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="distribution_price_impact_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Price Impact Analysis</h2>
                            <div data-html2canvas-ignore={true}>
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionOptions}
                                    className="grid_margin_right"
                                    value={selectedDimension}
                                    selectedOptions={selectedDimension}
                                    setStatedropdown={setSelectedDimension}
                                />
                                <SortOrderButton
                                    sortOrder={sortOrder}
                                    toggleSortOrder={toggleSortOrder}
                                    className="grid_margin_right"
                                />
                                <Button
                                    type="primary"
                                    className="width_fit_content"
                                    handleClick={() => setToggleTable(prev => !prev)}
                                >
                                    {toggleTable ? 'Chart' : 'Table'}
                                </Button>
                                <ExportDropdown
                                    sectionId="distribution_price_impact_analysis_section"
                                    sectionName="Price Impact Analysis"
                                    sectionData={toggleTable ? rawTableData.results : chart.data}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? tableContent : chartContent}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const Distribution = () => {
    // Get selected filters from Redux
    const selectedFilters = useSelector(state => state.globalFilter.selectedFilters);
    // Check if exactly one country, store_type, and manufacture are selected
    const isSingleCountry = Array.isArray(selectedFilters.country) && selectedFilters.country.length === 1;
    const isSingleStoreType = Array.isArray(selectedFilters.store_type) && selectedFilters.store_type.length === 1;
    const isSingleManufacturer = Array.isArray(selectedFilters.manufacture) && selectedFilters.manufacture.length === 1;
    const showGlobalFilterAlert = !(isSingleCountry && isSingleStoreType && isSingleManufacturer);

    // State to control alert visibility
    const [showAlert, setShowAlert] = useState(true);

    // Map panel state and logic (copied and adapted from Summary.jsx)
    const [showMapPanel, setShowMapPanel] = useState(false);
    const [mapGeoJson, setMapGeoJson] = useState(null);
    const [mapLoading, setMapLoading] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [hoverData, setHoverData] = useState({});
    const [hoverLoading, setHoverLoading] = useState(false);
    const [hoverError, setHoverError] = useState(null);

    // Get date and global filters from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);
    const mapPayload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        }
    }), [startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate, apiGlobalFilters]);
    const { post } = useFetch();

    // Listen for custom event from sidebar map button
    useEffect(() => {
        const handler = () => setShowMapPanel(true);
        window.addEventListener('open-map-panel', handler);
        return () => window.removeEventListener('open-map-panel', handler);
    }, []);

    // Fetch map data and hover data when map panel opens
    useEffect(() => {
        if (!showMapPanel) return;
        setMapLoading(true);
        setMapError(null);
        setHoverLoading(true);
        setHoverError(null);
        // Fetch map coordinates
        post('ri/map-coordinates', mapPayload)
            .then(response => {
                // Convert response to GeoJSON
                const features = Array.isArray(response)
                    ? response.map(item => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [item.longitude, item.latitude]
                        },
                        properties: {
                            ...item,
                            name: item.country_name || item.country_code
                        }
                    }))
                    : [];
                setMapGeoJson({ type: 'FeatureCollection', features });

                // Prepare country_ids for hover API
                const countryIds = features.map(f => f.properties.country_id).filter(Boolean);
                if (countryIds.length > 0) {
                    // Call hover API for all countries
                    const hoverPayload = {
                        global_filter: {
                            date_filter: mapPayload.global_filter.date_filter,
                            filter: {
                                ...mapPayload.global_filter.filter,
                                country_id: countryIds
                            }
                        }
                    };
                    post('ri/map-hover', hoverPayload)
                        .then(hoverResponse => {
                            // Store hover data by country_id
                            if (Array.isArray(hoverResponse)) {
                                const hoverDataObj = {};
                                hoverResponse.forEach(d => {
                                    if (d.country_id) hoverDataObj[d.country_id] = d;
                                });
                                setHoverData(hoverDataObj);
                            }
                        })
                        .catch(err => {
                            setHoverError(err);
                        })
                        .finally(() => setHoverLoading(false));
                } else {
                    setHoverData({});
                    setHoverLoading(false);
                }
            })
            .catch(err => {
                setMapError(err);
                setMapGeoJson(null);
                setHoverLoading(false);
            })
            .finally(() => setMapLoading(false));
    }, [showMapPanel, post, mapPayload]);

    // Hover handler for map markers
    const handleMarkerHover = (properties) => {
        if (!properties || !properties.country_id) return;
        setHoverLoading(true);
        setHoverError(null);
        // Add country_id to filter for hover
        const hoverPayload = {
            global_filter: {
                date_filter: mapPayload.global_filter.date_filter,
                filter: {
                    ...mapPayload.global_filter.filter,
                    country_id: [properties.country_id]
                }
            }
        };
        post('ri/map-hover', hoverPayload)
            .then(response => {
                // Response is array, find matching country_id
                if (Array.isArray(response)) {
                    const data = response.find(d => d.country_id === properties.country_id);
                    setHoverData(prev => ({ ...prev, [properties.country_id]: data }));
                }
            })
            .catch(err => {
                setHoverError(err);
            })
            .finally(() => setHoverLoading(false));
    };

    return (
        <Wrapper>
            <div className="dashboard-container">
                {/* LEFT: Map (only visible if showMapPanel) */}
                  <div
                                   className="dashboard-leftside"
                                   style={
                                       showMapPanel
                                        //    ? { width: 400, minWidth: 320, maxWidth: 440, transition: 'width 0.3s' }
                                        //    : { width: 0, minWidth: 0, maxWidth: 0, transition: 'width 0.3s', overflow: 'hidden', padding: 0 }
                                        
                            ? { flexBasis: 400, minWidth: 320, maxWidth: 440, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)' }
                            : { flexBasis: 0, minWidth: 0, maxWidth: 0, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden', padding: 0 }
                                   }
                               >
                                   {showMapPanel && (
                                       <div
                                           className="dashboard-left map-panel-ubuntu map-panel-custom"
               
                                       >
                                           {/* Header */}
                                           <div className="map-panel-header">
                                               <span className="map-panel-title">UAE</span>
                                               <button
                                                   onClick={() => setShowMapPanel(false)}
                                                   className="map-panel-close-btn"
                                                   aria-label="Close Map"
                                               >
                                                   ×
                                               </button>
                                           </div>
                                           {/* Map */}
                                           <div className="map-panel-map-container">
                                               {(mapLoading || hoverLoading) && (
                                                     <div className="map-panel-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
                                        <img
                                            src={require('../../../assets/images/gcc-map-loader.png')}
                                            alt="Loading map..."
                                            style={{ width: 240, height: 'auto', objectFit: 'contain' }}
                                        />
                                    </div>
                                               )}
                                               {(mapError || hoverError) && (
                                                   <div className="map-panel-error">Error loading map data</div>
                                               )}
                                               {!mapLoading && !hoverLoading && mapGeoJson && (
                                                   <MapComponent
                                                       geoJson={mapGeoJson}
                                                       mapLevel={1}
                                                       northPoint={["31.273904, 34.525594"]}
                                                       southPoint={["60.695581, 13.951499"]}
                                                       centerPoint={["44.556137,23.785713"]}
                                                       zoomLevel={2.5}
                                                       onChange={() => { }}
                                                       toolTipValues={["country_name", "country_code"]}
                                                       toolTipLabels={["Country Name", "Country Code"]}
                                                       growthDegrowth={null}
                                                       onMarkerHover={handleMarkerHover}
                                                       hoverData={hoverData}
                                                   />
                                               )}
                                           </div>
                                           {/* Legend */}
                                           <div className="map-panel-legend">
                                               <span className="map-panel-legend-item">
                                                   <span className="legend-dot green"></span>
                                                   Sales Value Growth
                                               </span>
                                               <span className="map-panel-legend-item">
                                                   <span className="legend-dot red"></span>
                                                   Sales Value De-Growth
                                               </span>
                                           </div>
                                       </div>
                                   )}
                               </div>

                {/* RIGHT: Dashboard content */}
                <div className="dashboard-right"
    style={{
        flex: 1,
        minWidth: 0
    }}>
                    <SelectedGlobalFilter />
                    {/* Show alert if not exactly one country, store_type, and manufacture are selected */}
                    {showGlobalFilterAlert && showAlert && (
                        <div className="compare-analysis-alert-wrapper" style={{ margin: "16px 0" }}>
                            <div
                                className="compare-analysis-alert"
                                style={{
                                    background: "#fffbe6",
                                    border: "2px solid #ffe58f",
                                    color: "#23282c",
                                    padding: "10px 16px",
                                    borderRadius: "8px ",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "14px",
                                    fontFamily: "Ubuntu, sans-serif",
                                    position: "relative"
                                }}
                            >
                                <span className="compare-alert-icon" style={{ marginRight: "8px", display: "flex", alignItems: "center" }}>
                                    <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                        <circle cx="10" cy="10" r="9" fill="#fffbe6" stroke="#faad14" strokeWidth="1.5" />
                                        <g>
                                            <path d="M10 6.5v4" stroke="#faad14" strokeWidth="1.5" strokeLinecap="round" />
                                            <circle cx="10" cy="13.1" r="1" fill="#faad14" />
                                        </g>
                                    </svg>
                                </span>
                                <span className="compare-alert-text" style={{ fontSize: "14px", fontFamily: "Ubuntu, sans-serif", }}>
                                    <strong style={{ fontWeight: 700 }}>Warning:</strong>
                                    <span style={{ marginLeft: 10, fontSize: "14px", fontFamily: "Ubuntu, sans-serif", color: "#595959" }}>
                                        TDP appears only when a single Country, Store Type, and Manufacturer are selected. </span>
                                </span>
                                <button
                                    className="compare-alert-close"
                                    onClick={() => setShowAlert(false)}
                                    aria-label="Dismiss"
                                    type="button"
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "#ad8b00",
                                        fontSize: "16px",
                                        fontWeight: 700,
                                        marginLeft: "auto",
                                        cursor: "pointer",
                                        lineHeight: 1
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="content">
                        <div className="content_section">
                            <PageHeader pageTitle={"Distribution"} />
                            <div className="page_content">
                                <DistributionPerformance />
                                <DistributionOverview />
                                {/* Only show TDPTrendAnalysis if all three are selected */}
                                {!showGlobalFilterAlert && <TDPTrendAnalysis />}
                                <DistributionEffectiveness />
                                <DistributionEfficiencyPerformance />
                                <SegmentWiseDistributionEfficiency />
                                {/* <SegmentDistributionEffectiveness/> */}
                                <DistributionImpactAnalysis />
                                <SummaryTable />
                                <CompareAnalysis />
                               
                            </div>
                        </div>
                    </div>
                </div>
            </div>           
        </Wrapper>
    )
}

export default Distribution